import { formatDateTime, timeAgo } from '../../utils/format';
import { formatPhone, getAvatarClass, getInitials, PLACEHOLDER } from '../users/userAdapter';
import { categoryMeta, LANGUAGES, sourceMeta, statusMeta } from './feedbackMeta';

/** Pure mapping from feedback API data to what the screen renders. Components only read these views. */

/** Only http(s) links are rendered (thumbnails, downloads). */
export function safeUrl(url) {
  if (typeof url !== 'string') return null;
  try {
    const parsed = new URL(url.trim());
    return parsed.protocol === 'https:' || parsed.protocol === 'http:' ? parsed.href : null;
  } catch {
    return null;
  }
}

/** 342 B, 342 KB, 1.4 MB. */
export function formatBytes(bytes) {
  if (typeof bytes !== 'number' || !Number.isFinite(bytes) || bytes < 0) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

const firstName = (name) => String(name ?? '').trim().split(/\s+/)[0] ?? '';

function toCustomerView(customer) {
  const name = customer.name?.trim() || null;
  return {
    isGuest: customer.isGuest,
    name: name ?? PLACEHOLDER,
    firstName: firstName(name),
    initials: getInitials(name),
    avatarClass: getAvatarClass(customer.userId ?? customer.id ?? customer.email),
    userId: customer.userId ?? null,
    email: customer.email?.trim() || null,
    phone: customer.phoneNumber ? formatPhone(customer.phoneNumber) : null,
  };
}

/** @param {import('./feedbackSchema').Attachment} a */
export function toAttachmentView(a) {
  const mime = (a.mimeType ?? '').toLowerCase();
  const name = a.fileName?.trim() || a.url.split('/').pop()?.split('?')[0] || 'attachment';
  return {
    id: String(a.id),
    url: safeUrl(a.url),
    rawUrl: a.url,
    fileName: name,
    sizeBytes: a.sizeBytes,
    sizeLabel: formatBytes(a.sizeBytes),
    isImage: mime.startsWith('image/') || /\.(jpe?g|png)$/i.test(name),
    isPdf: mime === 'application/pdf' || /\.pdf$/i.test(name),
  };
}

/**
 * @param {import('./feedbackSchema').FeedbackRow} row
 * @param {number} index  0-based position on this page
 */
export function toRowView(row, index, { page, limit }) {
  return {
    id: row.id,
    position: String((page - 1) * limit + index + 1).padStart(2, '0'),
    ticketNo: row.ticketNo,
    subject: row.subject.trim() || '(no subject)',
    preview: row.message.trim(),
    category: categoryMeta(row.category),
    status: statusMeta(row.status),
    source: sourceMeta(row.source),
    createdAgo: row.createdAt ? timeAgo(row.createdAt) : PLACEHOLDER,
    createdExact: row.createdAt ? formatDateTime(row.createdAt) : '',
    attachmentCount: row.attachmentCount,
    replyCount: row.replyCount,
    customer: toCustomerView(row.customer),
  };
}

export const toRowViews = (rows, paging) => rows.map((row, i) => toRowView(row, i, paging));

/** @param {import('./feedbackSchema').FeedbackDetail} d */
export function toDetailView(d) {
  return {
    id: d.id,
    ticketNo: d.ticketNo,
    subject: d.subject.trim() || '(no subject)',
    message: d.message,
    categoryKey: d.category,
    category: categoryMeta(d.category),
    statusKey: d.status,
    status: statusMeta(d.status),
    source: sourceMeta(d.source),
    appVersion: d.appVersion,
    deviceInfo: d.deviceInfo,
    language: d.lang ? LANGUAGES[d.lang.toLowerCase()] ?? d.lang.toUpperCase() : null,
    createdExact: d.createdAt ? formatDateTime(d.createdAt) : PLACEHOLDER,
    customer: toCustomerView(d.customer),
    attachments: d.attachments.map(toAttachmentView),
    replies: d.replies.map((r) => ({
      id: String(r.id),
      adminName: r.admin?.name?.trim() || 'Admin',
      createdExact: r.createdAt ? formatDateTime(r.createdAt) : '',
      subject: r.subject,
      body: r.body,
      toEmail: r.toEmail,
      delivered: r.emailStatus === 'sent',
      errorCode: r.errorCode,
      attachments: r.attachments.map(toAttachmentView),
    })),
    replyDefaults: {
      to: d.replyDefaults.to ?? d.customer.email ?? null,
      from: d.replyDefaults.from ?? null,
      subject: d.replyDefaults.subject || `Re: ${d.subject}`,
    },
  };
}
